'use client'

import { useEffect, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useAuthStore } from '@/stores/authStore'

// Ścieżki PUBLICZNE pod /installer (bez logowania). Wszystko inne wymaga tokenu.
// /installer = strona logowania; angebot/open = klient-facing; reset/register = konto.
const PUBLIC_PREFIXES = [
  '/installer/register',
  '/installer/reset',
  '/installer/angebot',
  '/installer/open',
]
function isPublic(path: string): boolean {
  if (path === '/installer' || path === '/installer/') return true
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))
}

/**
 * Twarda bramka logowania po stronie klienta.
 *
 * Aplikacja trzyma token w localStorage (Zustand persist), nie w ciasteczku —
 * więc middleware serwerowe nie może tego osłonić. Bez tej bramki udostępniony
 * link do chronionej strony (np. /installer/crm?tab=service) renderował
 * niezalogowanemu SZKIELET CRM zamiast ekranu logowania.
 *
 * KRYTYCZNE: decyzję podejmujemy dopiero PO hydratacji persist-store — inaczej
 * przy pierwszym renderze token=null wylogowałby wszystkich zalogowanych.
 */
export default function AuthGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || ''
  const router = useRouter()
  const token = useAuthStore((s) => s.token)

  // Inicjalizator MUSI być bezpieczny na serwerze (prerender) — nie wołamy tam
  // persist API (bywa undefined). Hydratację sprawdzamy dopiero w efekcie (klient).
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    const store = useAuthStore.persist
    if (!store) { setHydrated(true); return }
    if (store.hasHydrated()) { setHydrated(true); return }
    const unsub = store.onFinishHydration(() => setHydrated(true))
    return () => { unsub?.() }
  }, [])

  const publicPath = isPublic(pathname)

  useEffect(() => {
    if (publicPath || !hydrated) return
    if (!token) router.replace('/installer') // brak sesji → login (bez pokazywania struktury)
  }, [publicPath, hydrated, token, router, pathname])

  if (publicPath) return <>{children}</>

  // Chroniona strona: dopóki trwa hydratacja albo brak tokenu → neutralny ekran,
  // NIGDY szkielet CRM. Udostępniony link niezalogowanemu = pusto → redirect na login.
  if (!hydrated || !token) {
    return <div className="min-h-screen bg-white dark:bg-installer-bg" />
  }

  return <>{children}</>
}
